import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import { lastValueFrom, Observable, of } from 'rxjs';
import { debounceTime, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValue } from '@coin/shared/core/util-helpers';
import { TagModel } from '@coin/shared/communication/util';
import { Fn } from '@coin/shared/core/util-models';
import { CommunicationService } from '../../../communication/services/communication.service';

@Component({
  selector: 'admin-app-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrls: ['./tags-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TagsInputComponent
    }
  ],
  standalone: false
})
export class TagsInputComponent implements ControlValueAccessor, OnInit {
  private communicationService = inject(CommunicationService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild(MatTooltip) tooltip: MatTooltip;

  public removable = true;
  public selectable = true;
  public separatorKeysCodes: number[] = [ENTER, COMMA];
  public tagsInputCtrl = new FormControl<string>(undefined);
  public filteredTags: TagModel[];
  private allTags: TagModel[];
  public tagsInputForm: FormGroup<{ tagsInput: FormControl<string> }>;
  private isAddingBlocked = true;
  public loading = false;

  private controlValue = new ControlValue<TagModel[]>([]);

  public get disabled(): boolean {
    return this.controlValue.disabled;
  }

  public set disabled(value: boolean) {
    this.controlValue.disabled = value;
    if (this.tagsInputForm) {
      if (this.disabled) {
        this.tagsInputForm.disable();
      } else {
        this.tagsInputForm.enable();
      }
    }
  }

  public get value(): TagModel[] {
    return this.controlValue.getValue();
  }

  public set value(value: TagModel[]) {
    this.controlValue.setValue(value);
  }

  public writeValue(obj: TagModel[] | null): void {
    this.controlValue.setInitialValue(obj ?? []);
  }

  public registerOnChange(fn: Fn): void {
    this.controlValue.onChange = fn;
  }

  public registerOnTouched(fn: Fn): void {
    this.controlValue.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async ngOnInit(): Promise<void> {
    this.tagsInputForm = new FormGroup({
      tagsInput: new FormControl()
    });

    this.tagsInputCtrl.valueChanges
      .pipe(
        tap(() => {
          this.isAddingBlocked = true;
          this.loading = true;
        }),
        debounceTime(250),
        startWith(null),
        switchMap((name: string | null) => this._filter(name)),
        tap(labels => {
          this.allTags = labels;
          this.filteredTags = this._removeRedundantTags(labels);
          this.resetFlags();
        }),
        finalize(() => this.resetFlags()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.tagsInputCtrl.valueChanges
      .pipe(
        map(value => !!value),
        tap(show => !show && this.tooltip && this.tooltip.hide(0)),
        debounceTime(800),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(show => {
        if (show) {
          this.tooltip.show(0);
        }
      });
  }

  private addTag(tag: TagModel): void {
    this.value = [...this.value, tag];
    this.controlValue.markAsTouched();
  }

  private removeTag(tag: TagModel): void {
    this.value = this.value.filter(t => t.id !== tag.id);
    this.controlValue.markAsTouched();
  }

  public async add(event: MatChipInputEvent): Promise<void> {
    if (!this.isAddingBlocked) {
      this.loading = true;
      const input = event.chipInput.inputElement;
      const { value } = event;

      if ((value || '').trim()) {
        const foundTag: TagModel = this.allTags.find(tag => tag.name === value.trim());

        // check if tag already created
        if (foundTag) {
          if (this.filteredTags.find(tag => tag.name === foundTag.name)) {
            this.addTag(foundTag); // just add from dropdown
          } else {
            // restrict duplicates
            this.loading = false;
            return;
          }
        } else {
          // create tag
          this.isAddingBlocked = true;

          try {
            const res = await lastValueFrom(
              this.communicationService.postCommunicationsLabel({
                name: value.trim()
              })
            );
            this.addTag(res);
            this.isAddingBlocked = false;
          } catch (/* eslint-disable-line @typescript-eslint/no-unused-vars */ err) {
            this.resetFlags();
          }
        }
      }

      if (input) {
        input.value = '';
      }

      this.loading = false;
      this.tagsInputCtrl.setValue(null);
    }
  }

  public remove(tag: TagModel): void {
    this.removeTag(tag);
  }

  public selected(event: MatAutocompleteSelectedEvent): void {
    this.addTag(event.option.value);
    this.tagInput.nativeElement.value = '';
    this.tagsInputCtrl.setValue(null);
  }

  private _filter(value: string): Observable<TagModel[]> {
    if (!value) {
      return of([]);
    }

    return this.communicationService.getCommunicationsLabels(value);
  }

  private _removeRedundantTags(tags: TagModel[]): TagModel[] {
    const selectedTags = this.value ?? [];

    return tags.filter(
      tag => !selectedTags.find(selectedTag => selectedTag.id === tag.id)
    );
  }


  private resetFlags(): void {
    this.isAddingBlocked = false;
    this.loading = false;
  }
}
